import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabasePartner } from '../lib/supabase';

const PartnerAuthContext = createContext(null);

// Authentication state for the partner portal (travel agencies). This is a
// completely separate system from the customer OTP flow in BookingContext —
// it uses real Supabase Auth (email + password) through the isolated
// `supabasePartner` client only. Nothing here touches the anon `supabase`
// client or any customer-flow state.
export function PartnerAuthProvider({ children }) {
  // The Supabase Auth session (or null when signed out).
  const [partnerSession, setPartnerSession] = useState(null);
  // The matching row from the `partners` table (or null if none yet / signed out).
  const [partnerProfile, setPartnerProfile] = useState(null);
  // True only during the very first session check on mount, so guards can wait
  // before deciding to redirect to login.
  const [loadingAuth, setLoadingAuth] = useState(true);

  // Tracks the latest session-user id a profile fetch was started for, so a
  // slow earlier fetch can't overwrite the profile after auth has moved on.
  const fetchForUserRef = useRef(null);

  // Fetch the partner profile for a given auth user id. A logged-in auth user
  // might not yet have a `partners` row (edge cases during onboarding), so this
  // resolves to null gracefully rather than throwing.
  const fetchProfile = useCallback(async (userId) => {
    if (!userId) {
      setPartnerProfile(null);
      return null;
    }
    fetchForUserRef.current = userId;
    const { data, error } = await supabasePartner
      .from('partners')
      .select('*')
      .eq('auth_user_id', userId)
      .maybeSingle();

    // Ignore a stale response if auth has since changed to a different user.
    if (fetchForUserRef.current !== userId) return null;

    if (error) {
      console.error('Failed to load partner profile:', error);
      setPartnerProfile(null);
      return null;
    }
    setPartnerProfile(data ?? null);
    return data ?? null;
  }, []);

  // On mount: read any existing persisted session, then subscribe to auth
  // changes to keep session + profile in sync (sign in, sign out, token refresh).
  useEffect(() => {
    let active = true;

    supabasePartner.auth.getSession().then(({ data }) => {
      if (!active) return;
      const session = data?.session ?? null;
      setPartnerSession(session);
      if (session?.user?.id) {
        fetchProfile(session.user.id).finally(() => {
          if (active) setLoadingAuth(false);
        });
      } else {
        setLoadingAuth(false);
      }
    });

    const { data: sub } = supabasePartner.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      setPartnerSession(session ?? null);
      if (session?.user?.id) {
        fetchProfile(session.user.id);
      } else {
        fetchForUserRef.current = null;
        setPartnerProfile(null);
      }
    });

    return () => {
      active = false;
      sub?.subscription?.unsubscribe();
    };
  }, [fetchProfile]);

  // Sign in with email + password. Returns { error } so the login screen can
  // surface a message; on success onAuthStateChange drives the session/profile.
  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabasePartner.auth.signInWithPassword({
      email: (email || '').trim(),
      password: password || '',
    });
    return { data, error };
  }, []);

  // Sign out and clear the local profile immediately.
  const signOut = useCallback(async () => {
    const { error } = await supabasePartner.auth.signOut();
    fetchForUserRef.current = null;
    setPartnerProfile(null);
    return { error };
  }, []);

  // Re-fetch the profile for the current session (e.g. after registration or
  // an approval-status change).
  const refreshProfile = useCallback(async () => {
    const userId = partnerSession?.user?.id;
    return fetchProfile(userId);
  }, [partnerSession, fetchProfile]);

  const value = {
    partnerSession,
    partnerProfile,
    loadingAuth,
    signIn,
    signOut,
    refreshProfile,
  };

  return (
    <PartnerAuthContext.Provider value={value}>
      {children}
    </PartnerAuthContext.Provider>
  );
}

export function usePartnerAuth() {
  const ctx = useContext(PartnerAuthContext);
  if (!ctx) throw new Error('usePartnerAuth must be used within a PartnerAuthProvider');
  return ctx;
}
